import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { hasAccess } from '../config/emailWhitelist';

// Define User type manually for Firebase v10+
interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

interface AuthContextType {
  currentUser: User | null;
  signInWithGoogle: () => Promise<any>;
  logout: () => Promise<void>;
  isAuthorized: boolean;
  accessError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [accessError, setAccessError] = useState<string | null>(null);

  const signInWithGoogle = async (): Promise<any> => {
    try {
      // Configure Google provider for admin dashboard
      googleProvider.setCustomParameters({
        prompt: 'select_account' // Always show account selection
      });

      const result = await signInWithPopup(auth, googleProvider);

      // Check if user has access after successful authentication
      const userEmail = result.user?.email;
      if (userEmail && hasAccess(userEmail)) {
        setIsAuthorized(true);
        setAccessError(null);
        return result;
      } else {
        // User doesn't have access, sign them out
        await signOut(auth);
        setIsAuthorized(false);
        setAccessError('You do not have access to this service. Please contact your administrator.');
        throw new Error('Access denied');
      }
    } catch (error: any) {
      setIsAuthorized(false);
      if (error.message === 'Access denied') {
        throw error;
      } else {
        setAccessError('Authentication failed. Please try again.');
        throw error;
      }
    }
  };

  const logout = (): Promise<void> => {
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);

      if (user) {
        // Check if authenticated user has access
        const userEmail = user.email;
        if (userEmail && hasAccess(userEmail)) {
          setIsAuthorized(true);
          setAccessError(null);
        } else {
          setIsAuthorized(false);
          setAccessError('You do not have access to this service. Please contact your administrator.');
        }
      } else {
        setIsAuthorized(false);
        setAccessError(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signInWithGoogle,
    logout,
    isAuthorized,
    accessError,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 